/**
 * The agreement's editable fields — the SAME groups on both surfaces that write
 * them.
 *
 * Extracted from `new-page.tsx` on 2026-09-10, when Jeff asked for an edit
 * button in the drawer that "would open up a modal with the same 'new
 * agreement' form so edits could be made during sourcing". Two hand-rolled
 * copies of twenty fields is the drift item 69 documented, one screen later:
 * that divergence had already cost the agreement drawer its City and State. So
 * the create page and the edit modal both render this, and neither owns the
 * list.
 *
 * ── Customer and hotel are NOT here ────────────────────────────────────────
 * They are create-only, deliberately. Changing either makes it a different
 * agreement — a different customer's deal, or a deal at a different property —
 * not an edit of this one. The drawer's edit modal offers no way to do it,
 * which is the correct answer rather than a missing field.
 *
 * ── Neither is STATUS, on the edit surface ─────────────────────────────────
 * `showStatus` is true only on create, where the choice is which pre-contract
 * state a new record starts in. On an existing agreement, status moves through
 * the status chip, which is the one path that enforces `ALLOWED_TRANSITIONS`
 * and asks for a reason on a closure. A free `<select>` in this modal would
 * bypass the whole state machine — including Jeff's own rule that an agreement
 * cannot reach Loading except through Accepted — so the field is omitted and
 * the chip stays the only way to move one.
 *
 * The `na-*` element ids are kept exactly as they were: they are unique per
 * document and the two hosts never render together, so the create form's
 * existing tests and their `getByLabelText` gestures are untouched by this
 * extraction.
 */
import type { ReactNode } from 'react';
import {
  answeredCount,
  askedCount,
  type AmenityAnswers,
} from '@/features/hotel-supplier/amenities';

/** Statuses offered at creation — the pre-contract states only. */
export const CREATABLE_STATUSES = [
  { id: 'rfp_created', label: 'RFP — created' },
  { id: 'rfp_sent', label: 'RFP — sent to hotel' },
  { id: 'rfp_pending_acceptance', label: 'RFP — pending acceptance' },
  { id: 'rfp_accepted', label: 'RFP — accepted' },
  /*
   * `rebid_requested` is deliberately NOT offered at creation. It is a status
   * an agreement MOVES to once a bid has been seen and rejected, and it
   * requires a reason — neither of which is expressible on a record that does
   * not exist yet. The status chip is where it is reachable.
   */
] as const;

export const OWNER_TEAMS = [
  'sourcing',
  'supplier_relations',
  'gds_support',
  'online_tech',
] as const;

/** The flat, all-string field map both surfaces hold. */
export type AgreementFormValues = Record<string, string>;

export function AgreementFields({
  amenities,
  amenitiesSlot,
  form,
  mark,
  onOpenAmenities,
  set,
  showStatus = false,
}: {
  amenities: AmenityAnswers;
  form: AgreementFormValues;
  /** The extraction provenance marker. Absent on the edit surface, where no
   *  value came from a document. */
  mark?: (k: string) => ReactNode;
  onOpenAmenities: () => void;
  /**
   * Renders the amenity questions IN PLACE of the pop-out button.
   *
   * The create page is a page, so its questions are a modal — Jeff's
   * "pop-outs". The edit modal is already a modal, and `Modal` keeps no stack
   * (one Escape would close both, and two focus traps would fight), so there
   * the same questions render inline instead. Same list either way; only the
   * container differs.
   */
  amenitiesSlot?: ReactNode;
  set: (k: string, v: string) => void;
  showStatus?: boolean;
}) {
  const m = mark ?? (() => null);
  return (
    <>
      <div className="section">
        <div className="section-header">
          <span className="section-label">Where it stands</span>
        </div>
        <div className="section-body">
          <div className="form-grid">
            {showStatus && (
              <div>
                <label className="field-label" htmlFor="na-status">
                  Status
                  {m('status')}
                </label>
                <select
                  className="select"
                  data-testid="supplier-agreement-new-status"
                  id="na-status"
                  onChange={(e) => set('status', e.target.value)}
                  value={form.status}
                >
                  {CREATABLE_STATUSES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="field-label" htmlFor="na-owner">
                Owner team
                {m('owner_team')}
              </label>
              <select
                className="select"
                id="na-owner"
                onChange={(e) => set('owner_team', e.target.value)}
                value={form.owner_team}
              >
                {OWNER_TEAMS.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="na-tmc">
                TMC
                {m('tmc')}
              </label>
              <input
                className="input"
                id="na-tmc"
                onChange={(e) => set('tmc', e.target.value)}
                placeholder="Christopherson"
                value={form.tmc}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-negid">
                Negotiation ID
                {m('negotiation_id')}
              </label>
              <input
                className="input mono"
                id="na-negid"
                onChange={(e) => set('negotiation_id', e.target.value)}
                placeholder="from Smartsheet"
                value={form.negotiation_id}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="section">
        <div className="section-header">
          <span className="section-label">Term &amp; commercials</span>
        </div>
        <div className="section-body">
          <div className="form-grid">
            <div>
              <label className="field-label" htmlFor="na-tstart">
                Term starts
                {m('term_start')}
              </label>
              <input
                className="input"
                data-testid="supplier-agreement-new-term-start"
                id="na-tstart"
                onChange={(e) => set('term_start', e.target.value)}
                type="date"
                value={form.term_start}
              />
            </div>
            <div>
              {/* This is what puts the agreement in the Renewal queue 90 days
              before it lands, so it is worth filling in. */}
              <label className="field-label" htmlFor="na-tend">
                Term ends
                {m('term_end')}
              </label>
              <input
                className="input"
                data-testid="supplier-agreement-new-term-end"
                id="na-tend"
                onChange={(e) => set('term_end', e.target.value)}
                type="date"
                value={form.term_end}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-ccy">
                Currency
                {m('negotiated_currency')}
              </label>
              <input
                className="input"
                id="na-ccy"
                maxLength={3}
                onChange={(e) => set('negotiated_currency', e.target.value)}
                value={form.negotiated_currency}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-comm">
                Commission %{m('commission_rate_pct')}
              </label>
              <input
                className="input"
                id="na-comm"
                onChange={(e) => set('commission_rate_pct', e.target.value)}
                placeholder="10"
                value={form.commission_rate_pct}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-air">
                Airport codes
                {m('airport_codes')}
              </label>
              <input
                className="input mono"
                id="na-air"
                onChange={(e) => set('airport_codes', e.target.value)}
                placeholder="DTW,ORD"
                value={form.airport_codes}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="section">
        <div className="section-header">
          <span className="section-label">What we asked for</span>
          <span className="section-meta">Optional — the RFP header</span>
        </div>
        <div className="section-body">
          <div className="form-grid">
            <div>
              <label className="field-label" htmlFor="na-nights">
                Est. annual room nights
                {m('estimated_annual_room_nights')}
              </label>
              <input
                className="input"
                id="na-nights"
                onChange={(e) =>
                  set('estimated_annual_room_nights', e.target.value)
                }
                value={form.estimated_annual_room_nights}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-los">
                Avg length of stay
                {m('avg_length_of_stay_requested')}
              </label>
              <input
                className="input"
                id="na-los"
                onChange={(e) =>
                  set('avg_length_of_stay_requested', e.target.value)
                }
                placeholder="2.5"
                value={form.avg_length_of_stay_requested}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-lowest">
                Lowest fixed rate asked
                {m('lowest_fixed_rate_requested')}
              </label>
              <input
                className="input"
                id="na-lowest"
                onChange={(e) =>
                  set('lowest_fixed_rate_requested', e.target.value)
                }
                placeholder="175.00"
                value={form.lowest_fixed_rate_requested}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-dyn">
                Dynamic discount % asked
                {m('dynamic_discount_pct_requested')}
              </label>
              <input
                className="input"
                id="na-dyn"
                onChange={(e) =>
                  set('dynamic_discount_pct_requested', e.target.value)
                }
                placeholder="12"
                value={form.dynamic_discount_pct_requested}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="section">
        <div className="section-header">
          <span className="section-label">Contacts</span>
        </div>
        <div className="section-body">
          <div className="form-grid">
            <div>
              <label className="field-label" htmlFor="na-hsm">
                Hotel sales manager
                {m('hsm_name')}
              </label>
              <input
                className="input"
                id="na-hsm"
                onChange={(e) => set('hsm_name', e.target.value)}
                value={form.hsm_name}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-hsme">
                HSM email
                {m('hsm_email')}
              </label>
              <input
                className="input"
                id="na-hsme"
                onChange={(e) => set('hsm_email', e.target.value)}
                type="email"
                value={form.hsm_email}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-hsmp">
                HSM phone
                {m('hsm_phone')}
              </label>
              <input
                className="input"
                id="na-hsmp"
                onChange={(e) => set('hsm_phone', e.target.value)}
                value={form.hsm_phone}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-poc">
                Hotel contact
                {m('hotel_poc_name')}
              </label>
              <input
                className="input"
                id="na-poc"
                onChange={(e) => set('hotel_poc_name', e.target.value)}
                value={form.hotel_poc_name}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-poce">
                Contact email
                {m('hotel_poc_email')}
              </label>
              <input
                className="input"
                id="na-poce"
                onChange={(e) => set('hotel_poc_email', e.target.value)}
                type="email"
                value={form.hotel_poc_email}
              />
            </div>
          </div>
        </div>
      </div>
      {/*
       * THE AMENITY QUESTIONS, as a pop-out rather than thirteen more rows.
       *
       * Jeff, 2026-09-10: "when adding a new contract, all the amenity
       * questions should show up." They do — but inline they would be the
       * longest section on a form that already carries twenty fields, and
       * they are answered in one sitting from the hotel's reply rather than
       * typed alongside the term dates. So the form carries the SUMMARY and
       * the modal carries the questions, which is the same division the
       * contact and rate-audit flows settled on: "moving those workflows to
       * modals makes the drawer much more useful as reference info and allows
       * for us to have cleaner workspace… for when details need to be added."
       */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Amenities &amp; policies</span>
          <span className="section-meta" data-testid="na-amenities-count">
            {answeredCount(amenities)} of {askedCount(amenities)} answered
          </span>
        </div>
        <div className="section-body">
          {amenitiesSlot ?? (
            <>
              <button
                className="btn btn-outline"
                data-testid="na-amenities-open"
                onClick={onOpenAmenities}
                type="button"
              >
                {answeredCount(amenities) === 0
                  ? 'Answer the amenity questions'
                  : 'Review the amenity answers'}
              </button>
              <p
                className="page-intro"
                style={{ marginBottom: 0, marginTop: 12 }}
              >
                Breakfast, parking, resort fees, cancellation, renovations and
                the GDS loading confirmation. All optional — an agreement
                legitimately exists before the hotel has answered any of them.
              </p>
            </>
          )}
        </div>
      </div>
      <div className="section">
        {/* ITEM 36 — scoped in the label, and in a hint, because the example
        Stacie gave ("does NOT offer commissionable rates") is a fact about
        the PROPERTY rather than this deal, and typing it here files it
        where the next agreement at the same hotel will not find it. */}
        <div className="section-header">
          <span className="section-label">Agreement notes</span>
        </div>
        <div className="section-body">
          <div style={{ display: 'grid', gap: 12 }}>
            <p
              data-testid="na-notes-scope"
              style={{
                fontSize: 12.5,
                color: 'var(--text-muted)',
                margin: 0,
              }}
            >
              Both notes are saved against this agreement only. A fact about the
              property itself — &ldquo;does not offer commissionable
              rates&rdquo; — will not follow the hotel to another
              customer&rsquo;s agreement.
            </p>
            <div>
              <label className="field-label" htmlFor="na-inote">
                Internal note
                {m('internal_note')}
              </label>
              <textarea
                className="textarea"
                id="na-inote"
                onChange={(e) => set('internal_note', e.target.value)}
                rows={2}
                value={form.internal_note}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="na-hnote">
                From the hotel
                {m('hotel_note')}
              </label>
              <textarea
                className="textarea"
                id="na-hnote"
                onChange={(e) => set('hotel_note', e.target.value)}
                rows={2}
                value={form.hotel_note}
              />
            </div>
          </div>
        </div>
      </div>{' '}
    </>
  );
}
